import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { WatchQueryFetchPolicy } from '@apollo/client';
import { useHumioToken } from '../../../../shared/providers';
import {
  useSimpleSearchQuery,
  IsimpleSearchQuery,
} from '../../../investigations/queries';
import { useEntitiesContext } from '../EntitiesContext';
import {
  EntityCommunicationCounts,
  CommunicatingEntityDetail,
} from '../../types/entityCommunication';
import { processAggregatedEntityCommunicationEvents } from '../../utils/helpers';

interface QueryFunctionOptions {
  variables: {
    queryString: string;
    end: string;
    start: string;
    clientSessionId: string | null;
    tenantsForQuery: string[];
    size?: number;
  };
  fetchPolicy?: WatchQueryFetchPolicy;
}

export interface EntityCommunicationContextType {
  /** Aggregated counts of inbound/outbound entities */
  counts: EntityCommunicationCounts;
  /** Detailed list of inbound entities (contacted current entity) */
  inboundEntities: CommunicatingEntityDetail[];
  /** Detailed list of outbound entities (contacted by current entity) */
  outboundEntities: CommunicatingEntityDetail[];
  /** Loading state for counts query */
  isCountsLoading: boolean;
  /** Loading state for details query */
  isDetailsLoading: boolean;
  /** Combined loading state */
  isLoading: boolean;
}

const EntityCommunicationContext = createContext<
  EntityCommunicationContextType | undefined
>(undefined);

export const EntityCommunicationProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { token: sessionToken } = useHumioToken();
  const { selectedEntity } = useEntitiesContext();

  // Use normalized dates from selectedEntity (context handles fallback to global dates)
  const selectedEntityStart = selectedEntity?.start ?? 0;
  const selectedEntityEnd = selectedEntity?.end ?? 0;

  const entityName = selectedEntity?.entityName || '';
  const tenantId = selectedEntity?.tenantInfo?.tenant_id || '';

  // State for detailed entity lists
  const [inboundEntities, setInboundEntities] = useState<
    CommunicatingEntityDetail[]
  >([]);
  const [outboundEntities, setOutboundEntities] = useState<
    CommunicatingEntityDetail[]
  >([]);

  // Derive counts from entity lists - this guarantees counts always match
  const counts: EntityCommunicationCounts = useMemo(
    () => ({
      contactedCurrentEntity: inboundEntities.length,
      contactedByCurrentEntity: outboundEntities.length,
    }),
    [inboundEntities.length, outboundEntities.length],
  );

  // Build the details query string - fetch unique entities with aggregate query
  // When entity is as_client: get unique servers (id.resp_h) → OUTBOUND
  // When entity is as_server: get unique clients (id.orig_h) → INBOUND
  const detailsQueryString = useMemo(() => {
    if (!entityName) return '';
    return `#path=conn
| (id.orig_h="${entityName}" OR id.resp_h="${entityName}")
| case {
    id.orig_h = "${entityName}"
    | role := "as_client"
    | other_ip := id.resp_h
    | other_mac := resp_l2_addr;
    id.resp_h = "${entityName}"
    | role := "as_server"
    | other_ip := id.orig_h
    | other_mac := orig_l2_addr;
    *
    | role := "unknown";
}
| role != "unknown"
| groupBy([role, other_ip], function=[count(), selectLast(@timestamp), selectLast(other_mac), selectLast(id.resp_p), selectLast(proto)])`;
  }, [entityName]);

  // Memoize the onCompleted callback
  const handleDetailsQueryCompleted = useCallback(
    (data: IsimpleSearchQuery) => {
      const events = data.simpleSearchQuery?.events || [];

      const { inboundEntities, outboundEntities } =
        processAggregatedEntityCommunicationEvents(events);

      setInboundEntities(inboundEntities);
      setOutboundEntities(outboundEntities);
    },
    [],
  );

  // Memoize the onError callback
  const handleDetailsQueryError = useCallback(() => {
    setInboundEntities([]);
    setOutboundEntities([]);
  }, []);

  // Single aggregate query to fetch communication details - much more efficient than 10k records
  const [executeDetailsQuery, { loading: isDetailsLoading }] =
    useSimpleSearchQuery({
      notifyOnNetworkStatusChange: true,
      onCompleted: handleDetailsQueryCompleted,
      onError: handleDetailsQueryError,
    });

  // Execute queries function
  const executeQueries = useCallback(
    (
      queryFn: (options: QueryFunctionOptions) => Promise<unknown>,
      query: string,
    ) => {
      if (!entityName || !tenantId || !query) {
        return Promise.resolve();
      }

      const isValidDateRange =
        selectedEntityStart > 0 &&
        selectedEntityEnd > 0 &&
        selectedEntityStart < selectedEntityEnd;
      if (!isValidDateRange) {
        return Promise.resolve();
      }

      return queryFn({
        variables: {
          clientSessionId: sessionToken || null,
          queryString: query,
          end: selectedEntityEnd.toString(),
          start: selectedEntityStart.toString(),
          tenantsForQuery: [tenantId],
        },
        fetchPolicy: 'network-only', // Always fetch fresh data, don't use cache
      });
    },
    [
      entityName,
      tenantId,
      selectedEntityStart,
      selectedEntityEnd,
      sessionToken,
    ],
  );

  // Execute query when entity changes
  useEffect(() => {
    if (entityName && tenantId && selectedEntity?.timeoutId) {
      executeQueries(executeDetailsQuery, detailsQueryString);
    }
  }, [
    entityName,
    tenantId,
    selectedEntity?.timeoutId,
    detailsQueryString,
    executeQueries,
    executeDetailsQuery,
  ]);

  // Reset state when entity changes - runs before query effect due to effect order
  useEffect(() => {
    setInboundEntities([]);
    setOutboundEntities([]);
  }, [entityName]);

  const value = useMemo<EntityCommunicationContextType>(
    () => ({
      counts,
      inboundEntities,
      outboundEntities,
      isCountsLoading: isDetailsLoading,
      isDetailsLoading,
      isLoading: isDetailsLoading,
    }),
    [counts, inboundEntities, outboundEntities, isDetailsLoading],
  );

  return (
    <EntityCommunicationContext.Provider value={value}>
      {children}
    </EntityCommunicationContext.Provider>
  );
};

export const useEntityCommunicationContext = () => {
  const context = useContext(EntityCommunicationContext);
  if (!context) {
    throw new Error(
      'useEntityCommunicationContext must be used within an EntityCommunicationProvider',
    );
  }
  return context;
};

export { EntityCommunicationContext };
